"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
const images = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1470228/img/fire_extinguisher_inspection.png",
  alt: "Fire extinguisher inspection service",
  height: "h-64"
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1470228/img/kitchen_hood_suppression_system.png",
  alt: "Kitchen hood suppression system installation",
  height: "h-80"
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1470228/img/ansul_system_maintenance.png",
  alt: "Ansul system maintenance and testing",
  height: "h-72"
}, {
  id: 4,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1470228/img/sprinkler_system_inspection.png",
  alt: "Sprinkler system inspection",
  height: "h-64"
}, {
  id: 5,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1470228/img/commercial_kitchen_fire_protection.png",
  alt: "Commercial kitchen fire protection",
  height: "h-96"
}, {
  id: 6,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1470228/img/fire_extinguisher_recharging.png",
  alt: "Fire extinguisher recharging service",
  height: "h-72"
}, {
  id: 7,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1470228/img/emergency_fire_service_call.png",
  alt: "Emergency fire service response",
  height: "h-80"
}, {
  id: 8,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1470228/img/residential_fire_safety_equipment.png",
  alt: "Residential fire safety equipment",
  height: "h-64"
}, {
  id: 9,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1470228/img/fire_safety_certification.png",
  alt: "Fire safety certification and compliance",
  height: "h-72"
}];
export default function Gallery() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  const [isLightboxOpen, setIsLightboxOpen] = React.useState(false);
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setIsLightboxOpen(true);
  };
  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };
  const goToPrevious = () => {
    setCurrentImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
  };
  const goToNext = () => {
    setCurrentImageIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
  };
  React.useEffect(() => {
    if (!isLightboxOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goToPrevious();
      if (e.key === "ArrowRight") goToNext();
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLightboxOpen]);
  const prefersReducedMotion = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return <>
      <section ref={sectionRef} className="relative bg-light-background py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {}
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
        backgroundImage: "url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1470228/img/orange_texture_overlay.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        transform: prefersReducedMotion ? "none" : "translateY(calc(var(--scroll, 0) * -0.3px))"
      }} />

        <div className="relative z-10 max-w-7xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-display font-bold text-foreground text-center mb-12">
            Our Work Gallery
          </h2>

          {}
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
            {images.map((image, i) => <div key={image.id} data-index={i} className={`break-inside-avoid ${isVisible && !prefersReducedMotion ? "animate-fade-in-up" : "opacity-100"}`} style={{
            animationDelay: prefersReducedMotion ? "0ms" : `${i * 80}ms`
          }}>
                <Button variant="ghost" onClick={() => openLightbox(i)} className="group relative block w-full h-auto p-0 overflow-hidden rounded-lg shadow-soft transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:scale-105 hover:shadow-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" aria-label={`View ${image.alt}`}>
                  <div className={`${image.height} w-full bg-muted relative`}>
                    <img src={image.src} alt={image.alt} className="w-full h-full object-cover" loading="lazy" />
                    {}
                    <div className="absolute inset-0 border-2 border-transparent group-hover:border-primary opacity-0 group-hover:opacity-100 transition-all duration-[var(--duration-medium)] rounded-lg pointer-events-none" />
                  </div>
                </Button>
              </div>)}
          </div>
        </div>
      </section>

      {}
      <Dialog open={isLightboxOpen} onOpenChange={setIsLightboxOpen}>
        <DialogContent className="max-w-5xl w-full bg-dark-background/95 backdrop-blur-md border-none p-0 sm:p-6" showCloseButton={false}>
          <div className="relative">
            {}
            <DialogClose asChild>
              <Button variant="ghost" size="icon" className="absolute top-2 right-2 z-20 text-foreground-dark hover:bg-primary/20 rounded-full" aria-label="Close lightbox">
                <X className="size-6" />
              </Button>
            </DialogClose>

            {}
            <div className="relative flex items-center justify-center min-h-[60vh] max-h-[80vh]">
              <img src={images[currentImageIndex]?.src} alt={images[currentImageIndex]?.alt} className="max-w-full max-h-[80vh] object-contain rounded-lg" />
            </div>

            {}
            <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-4 pointer-events-none">
              <Button variant="ghost" size="icon" onClick={goToPrevious} className="pointer-events-auto bg-dark-background/70 hover:bg-primary/30 text-foreground-dark rounded-full shadow-medium" aria-label="Previous image">
                <ChevronLeft className="size-8" />
              </Button>
              <Button variant="ghost" size="icon" onClick={goToNext} className="pointer-events-auto bg-dark-background/70 hover:bg-primary/30 text-foreground-dark rounded-full shadow-medium" aria-label="Next image">
                <ChevronRight className="size-8" />
              </Button>
            </div>

            {}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-dark-background/80 text-foreground-dark px-4 py-2 rounded-full text-sm font-medium">
              {currentImageIndex + 1} / {images.length}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>;
}